/**
 * Dashboard helper components: section headers, profile cards, stats, goals, next steps.
 * Used only by the Dashboard page.
 */
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, Target, CheckCircle } from 'lucide-react';
import { Card, GradientText } from '../../components/ui/index.tsx';
import { UserAvatar } from '../../components/branding/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { NEXT_STEPS_CONFIG } from './dashboardConfig.tsx';
import type { FactFindGoals } from '../../../../shared/src/calculation/factFindTypes.ts';

interface SectionHeaderProps {
    title: string;
    showArrow?: boolean;
}

export const SectionHeader = ({ title, showArrow = true }: SectionHeaderProps) => (
  <div className="flex justify-between items-center pb-2 border-b border-gray-100">
    <h2 className="text-xl font-bold text-brand-navy ">{title}</h2>
    {showArrow && (
      <button className="text-blue-600 hover:text-blue-800 transition-all hover:translate-x-1">
        <ArrowRight size={22} />
      </button>
    )}
  </div>
);

interface DashboardPerson {
    name: string;
    health: string;
    age: number | null;
    initials: string;
    color1: string;
    color2: string;
}

interface ProfileCardProps {
    user: DashboardPerson;
}

export const ProfileCard = ({ user }: ProfileCardProps) => (
  <div className="flex items-center gap-4 p-4 rounded-xl bg-white border border-blue-100/50 hover:shadow-md transition-all duration-300">
    <div className="relative">
      <UserAvatar initials={user.initials} color1={user.color1} color2={user.color2} />
      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
    </div>
    <div className="flex-1">
      <p className="font-bold text-lg text-gray-800">{user.name || 'N/A'}</p>
      <p className="text-sm text-gray-500 flex items-center gap-1">
        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
        {user.health}
      </p>
    </div>
    <div className="text-right ml-auto pl-4 border-l border-slate-100">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Age</p>
        <p className="text-2xl font-bold text-slate-800 leading-tight">{user.age || '?'}</p>
    </div>
  </div>
);

interface InfoPillProps {
    icon: LucideIcon;
    text: string;
}

export const InfoPill = ({ icon: Icon, text }: InfoPillProps) => (
    <div className="flex items-center gap-2 bg-blue-500/10 text-blue-700 font-semibold px-4 py-2 rounded-full w-max border border-blue-200/50">
        <Icon size={18}/>
        <span>{text}</span>
    </div>
);

interface FinancialStatProps {
    title: string;
    value: number | string | null | undefined;
    isCurrency?: boolean;
    gradient?: string;
}

export const FinancialStat = ({ title, value, gradient = "bg-brand-navy" }: FinancialStatProps) => (
  <div className="group relative p-4 rounded-xl bg-white border border-gray-100 hover:border-gray-200 transition-all duration-300 hover:shadow-md">
    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide flex items-center gap-2 mb-2">
      <span className={`w-2 h-2 ${gradient} rounded-full shadow-sm`}></span>
      {title}
    </p>
    <p className="text-3xl font-extrabold text-brand-navy ">
        {value === null || value === undefined ? '$Nil' : formatCurrency(value)}
    </p>
  </div>
);

interface HeroMetricProps {
    label: string;
    value: number | null | undefined;
    icon?: LucideIcon;
    signed?: boolean;
}

/** Glassy metric tile used on the dark brand hero. value is a number (or null → $Nil). */
export const HeroMetric = ({ label, value, icon: Icon, signed = false }: HeroMetricProps) => {
  const isNeg = signed && (value || 0) < 0;
  return (
    <div className="rounded-2xl bg-white/10 hover:bg-white/[0.16] backdrop-blur-sm border border-white/15 p-5 transition-colors duration-300">
      <div className="flex items-center gap-2 mb-2 text-blue-100/80">
        {Icon && <Icon size={16} strokeWidth={2.2} />}
        {/* Sentence case, not tracked capitals (the mock-up's hero, 17 Oct 2026): a label a family member reads, not a dashboard stat. */}
        <span className="text-[13px] font-semibold">{label}</span>
      </div>
      <p className={`text-3xl font-extrabold tracking-tight ${isNeg ? 'text-rose-200' : 'text-white'}`}>
        {value === null || value === undefined ? '$Nil' : formatCurrency(value)}
      </p>
    </div>
  );
};

interface GoalItemProps {
    text: string;
}

export const GoalItem = ({ text }: GoalItemProps) => (
  <div className="flex items-center gap-3 mb-3 p-2 rounded-lg hover:bg-blue-50/50 transition-colors group">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-navy flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
      <CheckCircle size={16} className="text-white" fill="currentColor" />
    </div>
    <span className="font-medium text-gray-700 group-hover:text-gray-900">{text}</span>
  </div>
);

interface NextStepsCardProps {
    setActivePage: (page: string) => void;
    goals: FactFindGoals | undefined;
}

export const NextStepsCard = ({ setActivePage, goals }: NextStepsCardProps) => {
    const filteredSteps = NEXT_STEPS_CONFIG.filter(step => goals?.[step.goal]);
    return (
    <Card className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white p-6 relative overflow-hidden border-white/10 shadow-cardlg">
        <div className="absolute inset-0 z-0"></div>
        <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-brand-navy flex items-center justify-center">
                    <Target size={18} className="text-white" />
                </div>
                <h3 className="font-bold text-xl">Next Steps</h3>
            </div>
            <ul className="space-y-3">
                    {filteredSteps.map((step, i) => (
                    <li key={i} onClick={() => setActivePage(step.page)} className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 cursor-pointer group hover:shadow-lg">
                        <div className="flex-1">
                            <p className="text-sm text-gray-300 group-hover:text-white transition-colors">{step.text}</p>
                            <GradientText className="font-bold text-lg group-hover:scale-105 transition-transform inline-block">{step.link}</GradientText>
                        </div>
                        <ArrowRight size={20} className="text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all"/>
                    </li>
                ))}
            </ul>
        </div>
    </Card>
);
};
